import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { PhoneInput } from "@freightsimple/ui-core/Controls/PhoneInput";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { Tooltip } from "@freightsimple/ui-core/Feedback/Tooltip";
import type { EmergencyContactDetails } from "@freightsimple/generated-dashboard-openapi-client/models/EmergencyContactDetails";
import type { ReactNode } from "react";

import { formatErapPlanNumber } from "../Helpers/dangerousGoodsErapPlanNumber";
import type { EmergencyContactDetailsErrors } from "../Validators/dangerousGoodsErrorMessagesForEmergencyContact";
import { useLineItemsShipmentLimits } from "@/Shared/LineItems/Validators/useLineItemsShipmentLimits";

interface EmergencyContactInputProps {
  onUpdateEmergencyContactDetails: (_: EmergencyContactDetails) => void;
  emergencyContactDetails: EmergencyContactDetails;
  errorMessages: EmergencyContactDetailsErrors;
  forceValidation: boolean;
  // Canadian TDG shipments also need the consignor (TDG-responsible) name and,
  // when the goods have one, an Emergency Response Assistance Plan (ERAP).
  showCanadianTdgFields: boolean;
}

// A field label with a hover "?" explaining a Canadian TDG term.
function LabelWithInfo_({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <>
      {label}{" "}
      <Tooltip
        interactive
        variant="info"
        content={
          <Stack gap={2}>
            <T weight="bold">{label}</T>
            <T size="xs" color="secondary">
              {children}
            </T>
          </Stack>
        }
      />
    </>
  );
}

export function DangerousGoodsEmergencyContactInput(
  props: EmergencyContactInputProps,
) {
  const details = props.emergencyContactDetails;
  // The carrier's TDG box length comes from the served shipment limits.
  const shipmentLimits = useLineItemsShipmentLimits();

  return (
    <ShowFormErrors show={props.forceValidation}>
      <Stack gap={4} align="stretch">
        <FormItem
          label="Contact Name"
          error={props.errorMessages?.contactName}
          fillWidth
        >
          <TextInput
            value={details.contactName}
            onChange={(e) =>
              props.onUpdateEmergencyContactDetails({
                ...details,
                contactName: e.target.value,
              })
            }
            data-testid="emergency-contact-name"
            width="full"
          />
        </FormItem>
        <FormItem
          label="Contact Phone Number"
          error={props.errorMessages.contactPhoneNumber}
          fillWidth
        >
          <PhoneInput
            phoneNumber={details.contactPhoneNumber}
            extension={details.contactPhoneNumberExtension}
            onPhoneNumberChange={(value: string) => {
              props.onUpdateEmergencyContactDetails({
                ...details,
                contactPhoneNumber: value,
              });
            }}
            onExtensionChange={(value: string) => {
              props.onUpdateEmergencyContactDetails({
                ...details,
                contactPhoneNumberExtension: value,
              });
            }}
            // The ERAP line below is a second PhoneInput on this screen, so the
            // shared `name` alone can't single this one out.
            testId="emergency-contact-phone"
          />
        </FormItem>
        {props.showCanadianTdgFields && (
          <>
            <FormItem
              label={
                <LabelWithInfo_ label="TDG Responsible Name">
                  The person certifying the dangerous-goods declaration on the
                  bill of lading. This is a person, not the shipping business.
                </LabelWithInfo_>
              }
              error={props.errorMessages.tdgResponsibleName}
              fillWidth
            >
              <TextInput
                value={details.tdgResponsibleName ?? ""}
                onChange={(e) =>
                  props.onUpdateEmergencyContactDetails({
                    ...details,
                    tdgResponsibleName: e.target.value,
                  })
                }
                maxLength={
                  shipmentLimits.dangerousGoods.maxTdgResponsibleNameLength
                }
                data-testid="tdg-responsible-name"
                width="full"
              />
            </FormItem>
            <FormItem
              label={
                <LabelWithInfo_ label="ERAP Plan Number (if applicable)">
                  An Emergency Response Assistance Plan (ERAP) is a Transport
                  Canada-approved plan for responding to an accidental release
                  of certain higher-risk dangerous goods. Enter its reference
                  number if your shipment requires one (e.g. 2-1234-567).
                </LabelWithInfo_>
              }
              error={props.errorMessages.erapPlanNumber}
              fillWidth
            >
              <TextInput
                value={details.erapPlanNumber ?? ""}
                onChange={(e) =>
                  props.onUpdateEmergencyContactDetails({
                    ...details,
                    // Digits only; hyphens are inserted automatically as X-XXXX-XXX.
                    erapPlanNumber: formatErapPlanNumber(e.target.value),
                  })
                }
                data-testid="erap-plan-number"
                width="full"
              />
            </FormItem>
            <FormItem
              label={
                <LabelWithInfo_ label="ERAP Phone Number (if applicable)">
                  The 24-hour phone number used to activate the ERAP in an
                  emergency.
                </LabelWithInfo_>
              }
              error={props.errorMessages.erapPhoneNumber}
              fillWidth
            >
              <PhoneInput
                hideExtension
                phoneNumber={details.erapPhoneNumber}
                onPhoneNumberChange={(value: string) => {
                  props.onUpdateEmergencyContactDetails({
                    ...details,
                    erapPhoneNumber: value,
                  });
                }}
                onExtensionChange={() => {}}
                testId="erap-phone-number"
              />
            </FormItem>
          </>
        )}
      </Stack>
    </ShowFormErrors>
  );
}
